import React from 'react';
import PropTypes from 'prop-types';
import { Link, useLocation } from 'react-router-dom';
import NewsImage from '../components/NewsImage';
import '../styles/HorizontalNewsCarousel.css';

const CarouselSkeleton = ({ count = 5 }) => (
  <div className="horizontal-news-carousel__track" aria-hidden="true">
    {Array.from({ length: count }).map((_, index) => (
      <div
        key={index}
        data-testid="carousel-skeleton-card"
        className="horizontal-news-carousel__card horizontal-news-carousel__card--skeleton"
      >
        <div className="horizontal-news-carousel__image-skeleton" />
        <div className="horizontal-news-carousel__content">
          <div className="horizontal-news-carousel__line horizontal-news-carousel__line--date" />
          <div className="horizontal-news-carousel__line" />
          <div className="horizontal-news-carousel__line horizontal-news-carousel__line--short" />
          <div className="horizontal-news-carousel__badge-skeleton" />
        </div>
      </div>
    ))}
  </div>
);

CarouselSkeleton.propTypes = {
  count: PropTypes.number,
};

const HorizontalNewsCarousel = ({
  title,
  titleLink = null,
  news = [],
  initialLoading = false,
  showViews = false,
  skeletonCount = 5,
}) => {
  const location = useLocation();

  if (!initialLoading && news.length === 0) return null;

  return (
    <section className="horizontal-news-carousel">
      <div className="horizontal-news-carousel__inner">
        <div className="horizontal-news-carousel__header">
          <div className="panel-news">
            {titleLink ? (
              <Link to={titleLink} className="horizontal-news-carousel__heading-link">
                <h2>{title}</h2>
              </Link>
            ) : (
              <h2>{title}</h2>
            )}
          </div>
        </div>

        {initialLoading && news.length === 0 ? (
          <CarouselSkeleton count={skeletonCount} />
        ) : (
          <div className="horizontal-news-carousel__track">
            {news.map((item) => (
              <Link
                key={item.id}
                to={`/news/${item.id}`}
                state={{ from: location.pathname + location.search }}
                className="horizontal-news-carousel__card"
              >
                <div className="horizontal-news-carousel__image-wrap">
                  <NewsImage
                    news={item}
                    alt={item.title}
                    className="horizontal-news-carousel__image"
                    loading="lazy"
                  />
                </div>

                <div className="horizontal-news-carousel__content">
                  <div className="horizontal-news-carousel__date">
                    {item.day}.{item.month}.{item.year}
                    {item.time ? ` ${item.time}` : ''}
                  </div>

                  <h3 className="horizontal-news-carousel__title">
                    {item.title}
                  </h3>

                  <div className="horizontal-news-carousel__footer">
                    <span className="horizontal-news-carousel__category">
                      {Array.isArray(item.sections) && item.sections.length > 0
                        ? item.sections[0]
                        : item.category}
                    </span>

                    {showViews && (
                      <span className="horizontal-news-carousel__views">
                        {item.views} переглядів
                      </span>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

HorizontalNewsCarousel.propTypes = {
  title: PropTypes.string.isRequired,
  titleLink: PropTypes.string,
  news: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string,
    day: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    month: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    time: PropTypes.string,
    category: PropTypes.string,
    sections: PropTypes.arrayOf(PropTypes.string),
    views: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  })),
  initialLoading: PropTypes.bool,
  showViews: PropTypes.bool,
  skeletonCount: PropTypes.number,
};

export default HorizontalNewsCarousel;
